import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap'
import { Breadcrumbs, H4 } from '../../AbstractElements'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { useLayout } from '../../Hooks/useLayout'
import { Link, useNavigate } from 'react-router-dom'
import { getIdData } from '../../lib/profiles'
import { updateCompanyLogo, uploadCompanyLogo, getCompanyLogo } from '../../lib/company'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'

const AdminProfile = () => {
	const { user } = useAuth()
	const layout = useLayout()
	const navigate = useNavigate()
	const [selectedCompanyLogo, setSelectedCompanyLogo] = useState(null)
	const [adminInfo, setAdminInfo] = useState(null)
	const [companyLogo, setCompanyLogo] = useState('/defaultlogo.png')
	const [loading, setLoading] = useState(true)
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const timestamp = new Date().getTime()

	async function handleCompanyLogoChange() {
		if (!selectedCompanyLogo) {
			alert('Please select a logo first')
			return
		}

		if (!user || !user.companyId) {
			alert('Company information not available')
			return
		}

		// Validate image dimensions
		const img = new Image()
		img.src = URL.createObjectURL(selectedCompanyLogo)
		await new Promise((resolve) => (img.onload = resolve))

		if (img.width === img.height) {
			alert('Please upload a rectangle image for the company logo')
			return
		}

		if (img.width > 800 || img.height > 800) {
			alert('Image dimensions should not exceed 800x800 pixels')
			return
		}

		try {
			const fileExt = selectedCompanyLogo.name.split('.').pop()
			const fileName = `company_${user.companyId}_${timestamp}.${fileExt}`

			await uploadCompanyLogo(fileName, selectedCompanyLogo)
			await updateCompanyLogo(user.companyId, fileName)

			// Get the new logo
			const newLogoUrl = await getCompanyLogo(user.companyId)
			setCompanyLogo(newLogoUrl)
			setSelectedCompanyLogo(null)

			// Dispatch a custom event to notify other components
			const event = new CustomEvent('logoUpdated', { detail: { logoUrl: newLogoUrl } })
			window.dispatchEvent(event)

			alert('Company logo updated successfully!')
		} catch (error) {
			showToastMessage('Failed to update company logo')
			console.error('Error updating logo:', error)
		}
	}

	async function GetData() {
		try {
			if (!user) {
				console.warn('Profile not available yet')
				return
			}

			if (!user.companyId) {
				console.warn('Company ID not available')
				return
			}

			// Get admin info
			const info = await getIdData(user.id)
			setAdminInfo(info)

			// Get company logo
			const logoUrl = await getCompanyLogo(user.companyId)
			setCompanyLogo(logoUrl)
		} catch (error) {
			console.error('Error in GetData:', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (user && user.companyId) {
			GetData()
		}
	}, [user, user?.companyId])

	if (!user) {
		return (
			<>
				<Breadcrumbs mainTitle="Admin Profile" parent="Profile" title="Admin Profile Details" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card>
								<CardBody>
									<div className="text-center">
										<div className="alert alert-warning" role="alert">
											Please log in to view your profile.
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	if (loading) {
		return (
			<>
				<Breadcrumbs mainTitle="Admin Profile" parent="Profile" title="Admin Profile Details" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card>
								<CardBody>
									<div className="text-center">
										<div className="spinner-border text-primary" role="status">
											<span className="sr-only">Loading...</span>
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	return (
		<div className="position-relative">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<Breadcrumbs mainTitle="Admin Profile" parent="Profile" title="Admin Profile Details" />
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card>
							<CardHeader className="bg-primary text-white">
								<H4 attrH4={{ className: 'card-title mb-2 text-white' }}>
									<i className="fa fa-user-tie me-2"></i>
									Admin Profile Details
								</H4>
								<p className="mb-0 text-white-50">Manage your company logo and personal information.</p>
							</CardHeader>
							<CardBody>
								{/* Company Logo Section */}
								<div className="course-group mb-4 p-4 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
									<div className="d-flex flex-column flex-md-row align-items-center justify-content-between">
										<div className="d-flex align-items-center mb-3 mb-md-0">
											<div className="position-relative">
												{selectedCompanyLogo ? (
													<img
														src={URL.createObjectURL(selectedCompanyLogo)}
														alt="Company Logo Preview"
														className="img-fluid border border-3 border-white shadow"
														style={{
															maxWidth: '200px',
															borderRadius: '8px',
															transition: 'all 0.3s ease'
														}}
													/>
												) : (
													<img
														src={companyLogo}
														alt="Company Logo"
														className="img-fluid border border-3 border-white shadow"
														style={{
															maxWidth: '200px',
															borderRadius: '8px',
															transition: 'all 0.3s ease-in-out'
														}}
														onError={(e) => (e.target.src = '/defaultlogo.png')}
													/>
												)}
												{selectedCompanyLogo && (
													<div
														className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-success bg-opacity-75 rounded"
														style={{ borderRadius: '8px' }}
													>
														<i className="fa fa-check text-white fs-4"></i>
													</div>
												)}
											</div>
											<div className="ms-4">
												<h4 className="mb-2 text-primary">Company Logo</h4>
												<p className="mb-1 text-muted">Upload your company logo</p>
												<small className="text-muted">
													<i className="fa fa-info-circle me-1"></i>
													Rectangle image, max 800x800 pixels
												</small>
											</div>
										</div>
										<div className="d-flex flex-column flex-sm-row gap-2">
											<label className="btn btn-outline-primary btn-lg px-4 py-2">
												<i className="fa fa-upload me-2"></i>
												Select Logo
												<input type="file" accept="image/*" onChange={(e) => setSelectedCompanyLogo(e.target.files[0])} style={{ display: 'none' }} />
											</label>
											{selectedCompanyLogo && (
												<button onClick={handleCompanyLogoChange} className="btn btn-success btn-lg px-4 py-2" style={{ minWidth: '120px' }}>
													<i className="fa fa-save me-2"></i>
													Update Logo
												</button>
											)}
										</div>
									</div>
								</div>

								{/* Admin Information */}
								<div className="settings-widget">
									<div className="settings-inner-blk p-0">
										<div className="checkout-form personal-address add-course-info">
											<div className="personal-info-head mb-4">
												<h4 className="text-primary">
													<i className="fa fa-user me-2"></i>
													Admin Details
												</h4>
												<p className="text-muted mb-0">Your personal information and account settings</p>
											</div>
											<div className="row">
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">First Name</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{adminInfo ? adminInfo[0]?.name : 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Last Name</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{adminInfo ? adminInfo[0]?.surname : 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Phone</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{adminInfo ? adminInfo[0]?.phone : 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Email</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{adminInfo ? adminInfo[0]?.email : 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="update-profile d-flex flex-column flex-sm-row gap-3 justify-content-between mt-4">
														<button
															type="button"
															className="btn btn-primary btn-lg px-4 py-2"
															onClick={() => navigate(`/admin/profile-edit/${layout}`)}
															style={{ minWidth: '140px' }}
														>
															<i className="fa fa-edit me-2"></i>
															Update Profile
														</button>
														<Link
															to={`/admin/change-password/${layout}`}
															className="btn btn-warning btn-lg px-4 py-2 text-white"
															style={{ minWidth: '160px' }}
														>
															<i className="fa fa-key me-2"></i>
															Change Password
														</Link>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</div>
	)
}

export { AdminProfile }
